import React, { useEffect, useMemo } from "react";

import CopiedIcon from "@uui/CheckSquare";
import CopyIcon from "@uui/Copy01";
import EditIcon from "@uui/Edit05";
import SearchIcon from "@uui/SearchMd";
import { compact, map } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import FloatingNav, {
  FloatingNavActions,
  FloatingNavActionDivider,
  FloatingNavButton,
  FloatingNavInnerContainer,
  FloatingNavTab,
  FloatingNavTabs,
} from "@app/analytics/components/reports/FloatingNav";
import { EDITING_TOOLTIP } from "@app/analytics/MeetingReport/constants";
import useEditReport from "@app/analytics/MeetingReport/hooks/useEditReport";
import useSearch from "@app/analytics/MeetingReport/hooks/useSearch";
import MeetingReportSearchBox from "@app/analytics/MeetingReport/MeetingReportSearchBox";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import type {
  EditAccessLevel,
  RecapSection,
} from "@app/analytics/MeetingReport/types";
import type { SearchOptions } from "@app/analytics/MeetingReport/utils";
import * as Events from "@app/analytics/tracking/events";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import CopyButton from "@rui/CopyButton";
import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";
import Stack from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

import EditMenu from "./EditMenu";
import RecapSettings from "./RecapSettings";
import RestoreMenu from "./RestoreMenu";
import TranscriptFilters from "./TranscriptFilters";

interface MeetingReportRecapSubnavProps {
  copyContent: string;
  isNotetakerReport: boolean;
  isRestrictedReport: boolean;
  onSectionSelect: (section: RecapSection, scrollToSection: boolean) => void;
  selectedSection: RecapSection;
}

const CopyNotes = ({ content }: { content: string }) => {
  const handleClickCopyNotes = () => {
    Events.copyToClipboard({ name: "notes-copy" });
  };

  return (
    <CopyButton
      tooltip="Copy notes"
      copyValue={content}
      onClick={handleClickCopyNotes}
      renderIconButton={({ onClick }, copied) => (
        <FloatingNavButton
          onClick={onClick}
          Icon={copied ? CopiedIcon : CopyIcon}
        >
          {copied ? "Copied!" : "Copy"}
        </FloatingNavButton>
      )}
    />
  );
};

const EditButton = ({
  editAccessLevel,
  onClickEditSection,
}: {
  editAccessLevel: EditAccessLevel;
  onClickEditSection: (section: RecapSection) => void;
}) => {
  const { open, onClick, anchorEl, onClose } = useAnchorElement();

  if (editAccessLevel === "none") return null;

  const handleSelectSection: typeof onClickEditSection = (section) => {
    onClickEditSection(section);
    onClose();
  };

  const cannotEditTranscript = editAccessLevel !== "full";

  return (
    <>
      <FloatingNavActionDivider />
      <FloatingNavButton Icon={EditIcon} onClick={onClick}>
        Edit
      </FloatingNavButton>
      <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
        <MenuItem onClick={() => handleSelectSection("notes")}>
          Edit Notes
        </MenuItem>
        <TooltipWrapper
          title={EDITING_TOOLTIP.cannotEditSection}
          show={cannotEditTranscript}
        >
          <MenuItem
            disabled={cannotEditTranscript}
            onClick={() => handleSelectSection("transcript")}
          >
            Edit Transcript
          </MenuItem>
        </TooltipWrapper>
      </Menu>
    </>
  );
};

const MeetingReportRecapSubnav = ({
  copyContent,
  isNotetakerReport,
  isRestrictedReport,
  onSectionSelect,
  selectedSection,
}: MeetingReportRecapSubnavProps) => {
  const [queryParams] = useSearchParams();
  const searchQueryParam = queryParams.get("search");

  const { enterEditMode } = useEditReport();
  const { searchMode, setSearchMode, clearSearch } = useSearch();

  const { editAccessLevel, inEditMode, editingSection } = useEditStore(
    (state) => ({
      editAccessLevel: state.editAccessLevel,
      inEditMode: state.editingSection != null,
      editingSection: state.editingSection,
    })
  );

  const { filteredSpeakers, transcriptAvailable } = useTranscriptStore(
    (state) => ({
      filteredSpeakers: state.filteredSpeakers,
      transcriptAvailable: state.transcriptAvailable,
    })
  );

  useEffect(() => {
    if (!searchQueryParam) return;
    setSearchMode(inEditMode ? "replace" : "search");
  }, [searchMode, inEditMode, searchQueryParam]);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    section: RecapSection
  ) => onSectionSelect(section, true);

  const handleSelectEditSection: typeof enterEditMode = (section) => {
    enterEditMode(section);
    onSectionSelect(section, selectedSection !== section);
  };

  const searchOpen = Boolean(searchMode);

  const toggleSearchBox = () => {
    if (searchOpen) {
      setSearchMode(null);
      clearSearch();
    } else {
      setSearchMode("search");
    }
  };

  const searchOptions: SearchOptions = useMemo(
    () => ({
      speakers: compact(map("name", filteredSpeakers)),
      searchNotes: !inEditMode || editingSection === "notes",
      searchTranscript: !inEditMode || editingSection === "transcript",
      appendQueryParam: searchMode !== "replace",
      searchType: searchMode === "replace" ? "manual" : undefined,
    }),
    [filteredSpeakers, searchMode, inEditMode, editingSection]
  );

  const interactionsEnabled = !isRestrictedReport;

  // All the settings are related to the transcript/video; no point
  // showing them if they can't access either
  const showSettings = transcriptAvailable;
  const showCopyNotes = selectedSection === "notes";
  const showTranscriptMenuButtons =
    selectedSection === "transcript" && transcriptAvailable;

  const canEnterEditMode = editAccessLevel !== "none";
  const canRestoreReport = editAccessLevel === "full";

  return (
    <FloatingNav sx={{ p: 0 }} breakpoint={540}>
      <FloatingNavInnerContainer sx={{ p: 1 }}>
        {inEditMode ? (
          <EditMenu />
        ) : (
          <>
            <FloatingNavTabs value={selectedSection} onChange={handleTabChange}>
              <FloatingNavTab label="Notes" value="notes" />
              {!isNotetakerReport && (
                <FloatingNavTab label="Transcript" value="transcript" />
              )}
            </FloatingNavTabs>
            {interactionsEnabled && (
              <>
                {/* Secret clickable box to handle anyone who thinks that the empty space between the tabs
              and the buttons is the search box */}
                <Stack
                  flex={1}
                  sx={{ height: "100%" }}
                  onClick={() => setSearchMode("search")}
                />

                <FloatingNavActions spacing={0.5}>
                  <TooltipWrapper
                    placement="bottom"
                    title="Search notes"
                    show={!searchOpen}
                  >
                    <FloatingNavButton
                      Icon={SearchIcon}
                      onClick={toggleSearchBox}
                      active={searchOpen}
                    >
                      Search
                    </FloatingNavButton>
                  </TooltipWrapper>
                  {showSettings && (
                    <>
                      <FloatingNavActionDivider />
                      <RecapSettings />
                    </>
                  )}
                  {showCopyNotes && (
                    <>
                      <FloatingNavActionDivider />
                      <CopyNotes content={copyContent} />
                    </>
                  )}
                  {showTranscriptMenuButtons && (
                    <>
                      <FloatingNavActionDivider />
                      <TranscriptFilters />
                    </>
                  )}
                  {canEnterEditMode && (
                    <EditButton
                      editAccessLevel={editAccessLevel}
                      onClickEditSection={handleSelectEditSection}
                    />
                  )}
                  {canRestoreReport && <RestoreMenu />}
                </FloatingNavActions>
              </>
            )}
          </>
        )}
        <MeetingReportSearchBox
          onClose={() => setSearchMode(null)}
          // use the state prop instead of the "searching" variable because this prop controls whether
          // the input will autofocus; only want to autofocus when the user explicitly opens the search
          // box via the nav
          open={searchOpen}
          searchOptions={searchOptions}
        />
      </FloatingNavInnerContainer>
    </FloatingNav>
  );
};

export default MeetingReportRecapSubnav;
